import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Row, Col, Card, CardBody, Button, Input, FormGroup, Label,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Badge, Alert, Table, InputGroup,
} from 'reactstrap';
import { ArrowLeft, Plus, Trash2, Check, Film, Image, Mic, Database, Target,
         Play, X, CheckCircle, Copy, Search, Upload, ChevronDown, ChevronUp, Edit2 } from 'react-feather';
import CountUp from 'react-countup';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';
import { alertErro, alertConfirmar } from '../../utils/alerts';
import { useProspectAuth } from '../../Auth/ProspectAuthContext';

// ─── Constantes ───────────────────────────────────────────────────────────────

const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const REPORT_MODES = [
  { id: 'consolidated', label: 'Consolidado' },
  { id: 'by_campaign',  label: 'Por campanha' },
  { id: 'by_member',    label: 'Por vendedor' },
];


const DEFAULT_FLOW = [
  {
    type: 'isca',
    title: 'Isca',
    instruction: 'Você está iniciando um contato frio. Envie uma mensagem curta e natural que desperte curiosidade, sem revelar ainda o produto ou empresa. Use o nome do lead ({nome}) se disponível. NÃO se apresente ainda.',
    example: '"E aí, {nome}, tudo bem por aí? 😊"',
    send_without_permission: false,
  },
  {
    type: 'text',
    title: 'Apresentação',
    instruction: 'INDEPENDENTE do que o lead respondeu, apresente-se AGORA: nome, empresa e o que vocês resolvem — não o que vendem. Seja direto e simpático. Não prolongue o aquecimento nem faça perguntas ainda.',
    example: '"[Nome]! Sou [agente], da Topo 360. A gente ajuda empresas de qualquer segmento a prospectar novos clientes pelo WhatsApp de forma automática — sem depender de um time grande. Tenho algo interessante pra te mostrar. 😊"',
    send_without_permission: false,
  },
  {
    type: 'text',
    title: 'Pergunta de Dor',
    instruction: 'Faça UMA pergunta aberta que leve o lead a revelar o maior desafio ou dor do negócio dele relacionado ao que você resolve. Adapte a pergunta para o contexto do seu produto e segmento do lead. Não mencione o produto ainda. Não faça mais de uma pergunta.',
    example: '"Me conta uma coisa: como vocês buscam novos clientes hoje? A equipe prospecta ativamente ou dependem mais de indicação e de clientes que chegam por conta própria?"',
    send_without_permission: false,
  },
  {
    type: 'text',
    title: 'Qualificação',
    instruction: 'Use o que o lead respondeu para aprofundar o diagnóstico. Se o lead já tiver uma solução, explore onde ela falha ou onde ainda dói. Faça UMA pergunta que revele frequência do problema, custo ou resultado perdido — nunca ofereça solução ainda.',
    example: '"Entendi. E quando isso acontece, qual é o impacto pra vocês? Perdem o lead, atrasa o atendimento ou conseguem contornar na maioria das vezes?"',
    send_without_permission: false,
  },
  {
    type: 'text',
    title: 'Prova Social',
    instruction: 'PARE DE QUALIFICAR. Apresente AGORA um resultado real de cliente similar. Use o que o lead revelou para conectar o caso. Estrutura: empresa parecida → mesmo problema → resultado com número e prazo. PROIBIDO fazer qualquer pergunta neste step.',
    example: '"A gente tem clientes em vários segmentos nesse cenário. Um deles prospectava 30 contatos por semana na mão. Com o Topo Prospect, foi pra mais de 300 contatos/semana qualificando automaticamente — e agendou 18 reuniões em 3 semanas sem esforço manual."',
    send_without_permission: false,
  },
  {
    type: 'text',
    title: 'CTA',
    instruction: 'PARE DE QUALIFICAR. EXECUTE O CTA AGORA. Convide o lead a receber uma ligação de um consultor de negócios. Seja assumptivo — ofereça quando, não pergunte se. Nunca use "sem compromisso", "posso?" ou "o que acha?".',
    example: '"Com base no que você me contou, vale uma conversa rápida com nosso consultor — ele liga pra você em até 24h e mostra como funcionaria pro perfil de vocês. Quinta ou sexta fica melhor pra você?"',
    send_without_permission: false,
  },
];

const MEDIA_CFG = {
  video: { label: 'Vídeo', Icon: Film,  color: '#7366ff', bg: '#f0eeff', border: '#7366ff', gradient: 'linear-gradient(26deg,#7366ff,#a26cf8)', accept: '.mp4,.mov,.avi',             maxMB: 50 },
  photo: { label: 'Foto',  Icon: Image, color: '#54ba4a', bg: '#e8f5e9', border: '#51bb25', gradient: 'linear-gradient(26deg,#54ba4a,#29d9d5)', accept: '.jpg,.jpeg,.png,.gif,.webp', maxMB: 10 },
  audio: { label: 'Áudio', Icon: Mic,   color: '#ff9800', bg: '#fff3e0', border: '#f8a800', gradient: 'linear-gradient(26deg,#ff9800,#ffc107)', accept: '.mp3,.ogg,.aac,.m4a',       maxMB: 10 },
};

const MEDIA_SUMMARY = [
  { key: 'total', label: 'Total de mídias', Icon: Database, gradient: 'linear-gradient(26deg,#04b1e0,#6ddaf1)', colorClass: 'info'    },
  { key: 'video', label: 'Vídeos',          Icon: Film,     gradient: 'linear-gradient(26deg,#7366ff,#a26cf8)', colorClass: 'primary' },
  { key: 'photo', label: 'Fotos',           Icon: Image,    gradient: 'linear-gradient(26deg,#54ba4a,#29d9d5)', colorClass: 'success' },
  { key: 'audio', label: 'Áudios',          Icon: Mic,      gradient: 'linear-gradient(26deg,#ff9800,#ffc107)', colorClass: 'warning' },
];

function uid() { return `uid-${Date.now()}-${Math.random().toString(36).slice(2)}`; }

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function toSlug(str) {
  return str
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function CampanhaWizard() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const isEdit   = Boolean(id);
  const { user } = useProspectAuth();
  const isAdmin  = user?.role === 'admin';

  const [tab, setTab] = useState('0');

  // Admin
  const [tenants, setTenants]   = useState([]);
  const [tenantId, setTenantId] = useState('');

  // ── Aba 0 — Configurações ───────────────────────────────────────────────
  const [name, setName]                   = useState('');
  const [segment, setSegment]             = useState('');
  const [connections, setConns]           = useState([]);
  const [selectedConns, setSelConns]      = useState([]);
  const [lists, setLists]                 = useState([]);
  const [selectedLists, setSelLists]      = useState([]);
  const [listSearch, setListSearch]       = useState('');
  const [agents, setAgents]               = useState([]);
  const [prospectAgentId, setProspectAgentId] = useState(null);
  const [teamMembers, setTeamMembers]     = useState([]);

  const campaignNameRef = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => campaignNameRef.current?.focus({ preventScroll: true }), 250);
  }, []); // eslint-disable-line

  // Notificação
  const [assignMode, setAssignMode]       = useState('round_robin');
  const [fixedMemberId, setFixedMemberId] = useState('');
  const [notifyMemberIds, setNotifyIds]   = useState([]);
  const [reportMode, setReportMode]       = useState('consolidated');
  const [reportTime, setReportTime]       = useState('18:00');

  // Modais rápidos
  const [quickAgentModal, setQuickAgentModal]   = useState(false);
  const [quickAgentForm, setQuickAgentForm]     = useState({ name: '', company_name: '', city: '' });
  const [quickMemberModal, setQuickMemberModal] = useState(false);
  const [quickMemberForm, setQuickMemberForm]   = useState({ name: '', phone: '', role: 'vendedor' });

  // ── Aba 1 — Mídias (vinculadas à campanha) ──────────────────────────────
  const [campaignMedias, setCampaignMedias]       = useState([]);
  const [mediaLoading, setMediaLoading]           = useState(false);
  const [mediaTabFilter, setMediaTabFilter]       = useState('');
  const [mediaTabSearch, setMediaTabSearch]       = useState('');
  const [showUploadModal, setShowUploadModal]     = useState(false);
  const [mediaUploading, setMediaUploading]       = useState(false);
  const [mediaUploadPct, setMediaUploadPct]       = useState(0);
  const [mediaUploadForm, setMediaUploadForm]     = useState({ name: '', type: 'video', file: null });
  const [mediaDragOver, setMediaDragOver]         = useState(false);
  const [mediaCopied, setMediaCopied]             = useState(null);
  const [mediaPreview, setMediaPreview]           = useState(null);
  const mediaFileRef = useRef();

  // ── Aba 2 — Fluxo ──────────────────────────────────────────────────────
  const [flowSteps, setFlowSteps]     = useState([]);
  const [openSteps, setOpenSteps] = useState({});
  const [flowModal, setFlowModal]     = useState(null);
  const [flowForm, setFlowForm]       = useState({});
  const [flowEditIdx, setFlowEditIdx] = useState(null);
  const [disableAgentAfterFlow, setDisableAgentAfterFlow] = useState(false);

  // ── Aba 3 — Disparo ────────────────────────────────────────────────────
  const [schedule, setSchedule] = useState(
    DAYS.map((_, i) => ({ day_of_week: i, enabled: i > 0 && i < 6, start_time: '08:00', end_time: '18:00' }))
  );
  const [intervalMin, setIntMin]  = useState(20);
  const [intervalMax, setIntMax]  = useState(40);
  const [batchMin, setBatchMin]   = useState(12);
  const [batchMax, setBatchMax]   = useState(30);
  const [pauseMin, setPauseMin]   = useState(20);
  const [pauseMax, setPauseMax]   = useState(120);
  const [scheduledAt, setScheduled]   = useState('');
  const [launching, setLaunching]     = useState(false);
  const [scheduleModal, setScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate]   = useState('');

  // ── Geral ───────────────────────────────────────────────────────────────
  const [campaignId, setCampaignId] = useState(id || null);
  const [saving, setSaving]         = useState(false);
  const [tab0Error, setTab0Error]   = useState('');
  const [tab2Error, setTab2Error]   = useState('');
  const [daysEst, setDaysEst]       = useState('—');

  // ── Redirect admin ──────────────────────────────────────────────────────
  useEffect(() => {
    if (isAdmin) navigate('/campanhas', { replace: true });
  }, [isAdmin, navigate]);

  // ── Fetch inicial ───────────────────────────────────────────────────────
  useEffect(() => {
    if (isAdmin) return;
    if (isAdmin && !isEdit) api.get('/tenants').then(({ data }) => setTenants(data)).catch(() => {});

    api.get('/connections').then(({ data }) => {
      const connected = data.filter(c => c.status === 'connected');
      setConns(connected);
      if (!isEdit && connected.length === 1) setSelConns([connected[0].id]);
    }).catch(() => {});

    api.get('/lists').then(({ data }) => {
      setLists(data);
      if (!isEdit && data.length === 1) setSelLists([data[0].id]);
    }).catch(() => {});

    api.get('/agents').then(({ data }) => {
      setAgents(data);
      if (!isEdit && data.length === 1) setProspectAgentId(data[0].id);
    }).catch(() => {});

    api.get('/team').then(({ data }) => setTeamMembers(data)).catch(() => {});

    if (!isEdit) {
      setFlowSteps(DEFAULT_FLOW.map(s => ({ _uid: uid(), ...s })));
    }

    if (isEdit && id) {
      api.get(`/campaigns/${id}`).then(({ data }) => {
        setName(data.name);
        setSegment(data.segment || '');
        setSelConns(data.connection_ids || []);
        setSelLists(data.list_ids || []);
        setProspectAgentId(data.prospect_agent_id || null);
        setDisableAgentAfterFlow(!!data.disable_agent_after_flow);
      }).catch(() => {});

      api.get(`/campaigns/${id}/notification`).then(({ data }) => {
        if (data) {
          setAssignMode(data.assignment_mode || 'round_robin');
          setFixedMemberId(data.fixed_member_id || '');
          setNotifyIds(data.notify_member_ids || []);
          setReportMode(data.report_mode || 'consolidated');
          setReportTime(data.report_time || '18:00');
        }
      }).catch(() => {});

      api.get(`/campaigns/${id}/flow`).then(({ data }) => {
        if (data.length) setFlowSteps(data.map(s => ({
          ...s,
          _uid: `id-${s.id}`,
          title: s.title || s.content?.split('\n')[0]?.slice(0, 40) || `Etapa ${s.step_order}`,
          instruction: s.instruction || s.content || '',
          example: s.example || '',
        })));
        else setFlowSteps(DEFAULT_FLOW.map(s => ({ _uid: uid(), ...s })));
      }).catch(() => setFlowSteps(DEFAULT_FLOW.map(s => ({ _uid: uid(), ...s }))));

      api.get(`/campaigns/${id}/schedule`).then(({ data }) => {
        if (data.length) {
          setSchedule(data.map(d => ({ ...d, start_time: d.start_time?.slice(0, 5) || '08:00', end_time: d.end_time?.slice(0, 5) || '18:00' })));
          const f = data[0];
          if (f) {
            setIntMin(f.interval_min || 20); setIntMax(f.interval_max || 40);
            setBatchMin(f.batch_min || 12); setBatchMax(f.batch_max || 30);
            setPauseMin(f.pause_min || 20); setPauseMax(f.pause_max || 120);
          }
        }
      }).catch(() => {});

      fetchCampaignMedias(id);
    }
  }, [id, isEdit]); // eslint-disable-line

  // ── Fetch mídias da campanha ─────────────────────────────────────────────
  async function fetchCampaignMedias(cid) {
    const cIdToUse = cid || campaignId;
    if (!cIdToUse) return;
    setMediaLoading(true);
    try {
      const params = mediaTabFilter ? `?type=${mediaTabFilter}` : '';
      const { data } = await api.get(`/campaigns/${cIdToUse}/medias${params}`);
      setCampaignMedias(data);
    } catch (e) { console.error(e); }
    finally { setMediaLoading(false); }
  }

  useEffect(() => {
    if (campaignId) fetchCampaignMedias();
  }, [mediaTabFilter]); // eslint-disable-line

  // Re-fetch mídias sempre que o usuário entra na aba Mídias/Fluxo ou campaignId aparece
  useEffect(() => {
    if ((tab === '1' || tab === '2') && campaignId) fetchCampaignMedias(campaignId);
  }, [tab, campaignId]); // eslint-disable-line

  // ── Estimativa de dias ──────────────────────────────────────────────────
  const totalContacts = lists.filter(l => selectedLists.includes(l.id)).reduce((s, l) => s + (l.total_contacts || 0), 0);
  const filteredLists = lists.filter(l => !listSearch || l.name.toLowerCase().includes(listSearch.toLowerCase()));

  useEffect(() => {
    const activeDays = schedule.filter(d => d.enabled);
    if (!activeDays.length || totalContacts <= 0) { setDaysEst('—'); return; }
    const minPorDia = activeDays.reduce((sum, d) => {
      const [sh, sm] = (d.start_time || '08:00').split(':').map(Number);
      const [eh, em] = (d.end_time   || '18:00').split(':').map(Number);
      return sum + Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
    }, 0) / activeDays.length;
    const intMedio = (intervalMin + intervalMax) / 2;
    const loteMedio = (batchMin + batchMax) / 2;
    const pausaMed = (pauseMin + pauseMax) / 2;
    const segsPorDisparo = intMedio + (pausaMed * 60 / loteMedio);
    const capDia = segsPorDisparo > 0 ? (minPorDia * 60) / segsPorDisparo : 0;
    setDaysEst(capDia > 0 ? Math.ceil(totalContacts / capDia) : '—');
  }, [intervalMin, intervalMax, batchMin, batchMax, pauseMin, pauseMax, schedule, totalContacts]);

  // ── Aba 0: Salvar e continuar ───────────────────────────────────────────

  async function saveTab0() {
    setTab0Error('');
    if (!name.trim()) { setTab0Error('Informe o nome da campanha.'); return; }
    if (!selectedConns.length) { setTab0Error('Selecione pelo menos 1 conexão WhatsApp.'); return; }
    if (!selectedLists.length) { setTab0Error('Selecione pelo menos 1 lista de contatos.'); return; }
    if (!prospectAgentId) { setTab0Error('Selecione um agente de IA responsável.'); return; }

    setSaving(true);
    try {
      let cid = campaignId;

      if (!cid) {
        const { data } = await api.post('/campaigns', {
          name, segment: segment.trim() || null,
          connection_ids: selectedConns, list_ids: selectedLists,
          prospect_agent_id: prospectAgentId,
          tenant_id: isAdmin ? parseInt(tenantId) : user?.tenant_id,
        });
        cid = data.id;
        setCampaignId(cid);
      } else {
        await api.put(`/campaigns/${cid}`, {
          name, segment: segment.trim() || null,
          connection_ids: selectedConns, list_ids: selectedLists,
          prospect_agent_id: prospectAgentId,
        });
      }

      await api.post(`/campaigns/${cid}/notification`, {
        assignment_mode: assignMode,
        fixed_member_id: assignMode === 'fixed' ? (fixedMemberId || null) : null,
        notify_member_ids: notifyMemberIds,
        report_mode: reportMode,
        report_time: reportTime,
      }).catch(() => {});

      await fetchCampaignMedias(cid);
      setTab('1');
    } catch (e) {
      alertErro('Erro ao salvar', e.response?.data?.error || 'Não foi possível salvar a campanha');
    } finally {
      setSaving(false);
    }
  }

  // ── Aba 1: Mídias ─────────────────────────────────────────────────────

  const mediaCounts = {
    total: campaignMedias.length,
    video: campaignMedias.filter(m => m.type === 'video').length,
    photo: campaignMedias.filter(m => m.type === 'photo').length,
    audio: campaignMedias.filter(m => m.type === 'audio').length,
  };

  const filteredTabMedias = campaignMedias.filter(m =>
    !mediaTabSearch || m.name.toLowerCase().includes(mediaTabSearch.toLowerCase())
  );

  const mediaSlug = toSlug(mediaUploadForm.name);
  const mediaSlugValid = !mediaSlug || /^[a-z0-9_-]+$/.test(mediaSlug);

  function openUploadModal() {
    setMediaUploadForm({ name: '', type: 'video', file: null });
    setMediaUploadPct(0);
    setShowUploadModal(true);
  }

  function onMediaFileChange(file) {
    if (!file) return;
    const slug = file.name.replace(/\.[^.]+$/, '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setMediaUploadForm(prev => ({ ...prev, file, name: prev.name ? prev.name : slug }));
  }

  async function handleMediaUpload() {
    if (!mediaUploadForm.file)       return alertErro('Arquivo obrigatório', 'Selecione um arquivo para enviar.');
    if (!mediaUploadForm.name.trim()) return alertErro('Nome obrigatório', 'Informe um nome/ID para a mídia.');
    if (!mediaSlugValid)              return alertErro('Nome inválido', 'Use apenas letras minúsculas, números, - e _.');

    const cfg      = MEDIA_CFG[mediaUploadForm.type] || MEDIA_CFG.video;
    const maxBytes = cfg.maxMB * 1024 * 1024;
    if (mediaUploadForm.file.size > maxBytes)
      return alertErro('Arquivo muito grande', `Máximo ${cfg.maxMB}MB para ${cfg.label.toLowerCase()}.`);

    const fd = new FormData();
    fd.append('file', mediaUploadForm.file);
    fd.append('name', mediaSlug);
    fd.append('type', mediaUploadForm.type);

    setMediaUploading(true);
    setMediaUploadPct(0);
    try {
      await api.post(`/campaigns/${campaignId}/medias/upload`, fd, {
        onUploadProgress: e => setMediaUploadPct(Math.round((e.loaded / e.total) * 100)),
      });
      setShowUploadModal(false);
      setMediaUploadForm({ name: '', type: 'video', file: null });
      fetchCampaignMedias(campaignId);
    } catch (e) {
      alertErro('Erro no upload', e.response?.data?.error || 'Tente novamente.');
    } finally {
      setMediaUploading(false);
      setMediaUploadPct(0);
    }
  }

  async function handleMediaDelete(m) {
    const ok = await alertConfirmar('Remover mídia?', `"${m.name}" será excluída permanentemente.`);
    if (!ok) return;
    try {
      await api.delete(`/campaigns/${campaignId}/medias/${m.id}`);
      fetchCampaignMedias(campaignId);
    } catch (e) {
      alertErro('Erro ao remover', e.response?.data?.error || '');
    }
  }

  function copyMediaUrl(url) {
    navigator.clipboard.writeText(window.location.origin + url).then(() => {
      setMediaCopied(url);
      setTimeout(() => setMediaCopied(null), 2000);
    });
  }

  // ── Aba 2: Fluxo ───────────────────────────────────────────────────────

  function openAddModal() {
    setFlowForm({ type: 'text', title: '', instruction: '', example: '', send_without_permission: false });
    setFlowEditIdx(null);
    setFlowModal('add');
  }

  function openEditModal(index) {
    setFlowForm({ ...flowSteps[index] });
    setFlowEditIdx(index);
    setFlowModal('edit');
  }

  function saveFlowModal() {
    if (!flowForm.title?.trim()) return;
    const step = {
      _uid: flowModal === 'add' ? uid() : (flowSteps[flowEditIdx]?._uid || uid()),
      ...flowForm,
    };
    if (flowModal === 'add') {
      setFlowSteps(prev => [...prev, step]);
    } else {
      setFlowSteps(prev => prev.map((s, i) => i === flowEditIdx ? step : s));
    }
    setFlowModal(null);
  }

  async function removeStep(index) {
    const ok = await alertConfirmar('Remover etapa?', 'Tem certeza que deseja remover esta etapa do fluxo?');
    if (!ok) return;
    const uid = flowSteps[index]?._uid;
    setFlowSteps(prev => prev.filter((_, i) => i !== index));
    if (uid) setOpenSteps(prev => { const next = { ...prev }; delete next[uid]; return next; });
  }

  function updateStep(index, field, value) {
    setFlowSteps(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  }

  function moveStep(index, dir) {
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= flowSteps.length) return;
    setFlowSteps(prev => {
      const arr = [...prev];
      [arr[index], arr[newIdx]] = [arr[newIdx], arr[index]];
      return arr;
    });
  }

  function handleDragEnd(result) {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const reordered = Array.from(flowSteps);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setFlowSteps(reordered);
  }

  async function persistFlow() {
    if (!campaignId || flowSteps.length === 0) return;
    await api.post(`/campaigns/${campaignId}/flow`, {
      disable_agent_after_flow: disableAgentAfterFlow,
      steps: flowSteps.map((s, i) => ({
        step_order: i + 1,
        type: s.type,
        title: s.title || null,
        instruction: s.instruction || null,
        example: s.example || null,
        content: s.instruction || null,
        media_id: s.media_id || null,
        media_name: s.media_name || null,
        media_type: s.media_type || null,
        send_without_permission: s.send_without_permission || false,
        step_uid: s._uid || null,
      })),
    });
  }

  async function saveTab2() {
    setTab2Error('');
    if (!campaignId) { setTab2Error('Salve a aba Configurações antes de continuar.'); return; }
    try {
      await persistFlow();
      setTab('3');
    } catch (e) {
      alertErro('Erro ao salvar fluxo', e.response?.data?.error || '');
    }
  }

  // ── Aba 3: Disparo ────────────────────────────────────────────────────

  function updateDay(dow, field, value) {
    setSchedule(prev => prev.map(d => d.day_of_week === dow ? { ...d, [field]: value } : d));
  }

  async function saveSchedule() {
    if (!campaignId) return;
    await api.put(`/campaigns/${campaignId}/schedule`, {
      days: schedule, interval_min: intervalMin, interval_max: intervalMax,
      batch_min: batchMin, batch_max: batchMax, pause_min: pauseMin, pause_max: pauseMax,
    }).catch(e => console.error('Erro schedule:', e.message));
  }

  async function launch(status, scheduled) {
    setLaunching(true);
    try {
      await persistFlow().catch(e => console.error('Erro ao salvar fluxo no launch:', e.message));
      await saveSchedule();
      await api.put(`/campaigns/${campaignId}/status`, { status, scheduled_at: scheduled || undefined });
      navigate(`/campanhas/${campaignId}`);
    } catch (e) {
      alertErro('Erro ao lançar', e.response?.data?.error || '');
      setLaunching(false);
    }
  }

  async function handleSaveDraft() {
    if (!campaignId) return;
    try {
      await saveSchedule();
      await api.put(`/campaigns/${campaignId}/status`, { status: 'draft' });
      navigate('/campanhas');
    } catch (e) {
      alertErro('Erro ao salvar', e.response?.data?.error || '');
    }
  }

  async function handleSchedule() {
    if (!scheduleDate) return;
    setScheduleModal(false);
    await launch('scheduled', scheduleDate);
  }

  // ── Helpers rápidos ───────────────────────────────────────────────────

  function toggleConn(cid) { setSelConns(p => p.includes(cid) ? p.filter(i => i !== cid) : [...p, cid]); }
  function toggleList(lid) { setSelLists(p => p.includes(lid) ? p.filter(i => i !== lid) : [...p, lid]); }
  function toggleNotify(mid) { setNotifyIds(p => p.includes(mid) ? p.filter(i => i !== mid) : [...p, mid]); }

  async function handleQuickAgent() {
    if (!quickAgentForm.name.trim()) return;
    try {
      const { data } = await api.post('/agents', quickAgentForm);
      setAgents(prev => [...prev, data]);
      setProspectAgentId(data.id);
      setQuickAgentModal(false);
      setQuickAgentForm({ name: '', company_name: '', city: '' });
    } catch (e) { alertErro('Erro', e.response?.data?.error || ''); }
  }

  async function handleQuickMember() {
    if (!quickMemberForm.name.trim() || !quickMemberForm.phone.trim()) return;
    try {
      const { data } = await api.post('/team', quickMemberForm);
      setTeamMembers(prev => [...prev, data]);
      setQuickMemberModal(false);
      setQuickMemberForm({ name: '', phone: '', role: 'vendedor' });
    } catch (e) { alertErro('Erro', e.response?.data?.error || ''); }
  }

  // ── Visual helpers ────────────────────────────────────────────────────

  function tabDone(i) { return parseInt(tab) > i; }
  function tabActive(i) { return parseInt(tab) === i; }
  const tabLabels = ['Configurações', 'Mídias', 'Fluxo de Abordagem', 'Disparo em Massa'];

  const canSubmitUpload = !mediaUploading && !!mediaUploadForm.file && !!mediaUploadForm.name.trim() && mediaSlugValid;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <Breadcrumbs mainTitle={isEdit ? 'Editar Campanha' : 'Nova Campanha'} parent='Topo Prospect' title='Campanhas' />
      <Container fluid>

        {/* Header */}
        <div className='d-flex align-items-center gap-2 mb-4'>
          <Button color='light' size='sm' onClick={() => navigate('/campanhas')}><ArrowLeft size={16} /></Button>
          <h4 className='mb-0 fw-bold'>{isEdit ? 'Editar Campanha' : 'Nova Campanha'}</h4>
        </div>

        {/* Wizard tabs */}
        <Card className='mb-4'>
          <CardBody className='py-2'>
            <Nav tabs className='border-0 gap-1'>
              {tabLabels.map((label, i) => (
                <NavItem key={i}>
                  <NavLink
                    className={`d-flex align-items-center gap-2 ${tabActive(i) ? 'active' : ''}`}
                    onClick={() => (i < parseInt(tab) || campaignId) ? setTab(String(i)) : null}
                    style={{ cursor: i > parseInt(tab) && !campaignId ? 'not-allowed' : 'pointer', opacity: i > parseInt(tab) && !campaignId ? 0.4 : 1 }}
                  >
                    <span className={`badge rounded-pill ${tabDone(i) ? 'bg-success' : tabActive(i) ? 'bg-primary' : 'bg-secondary'}`}
                      style={{ width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>
                      {tabDone(i) ? <Check size={10} /> : i + 1}
                    </span>
                    <span className='d-none d-sm-inline'>{label}</span>
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </CardBody>
        </Card>

        <TabContent activeTab={tab}>

          {/* ════════════════════════════════════════════════════════════
              ABA 0 — CONFIGURAÇÕES
          ════════════════════════════════════════════════════════════ */}
          <TabPane tabId='0'>
            <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>

              {isAdmin && !isEdit && (
                <Card><CardBody>
                  <h6 className='fw-semibold mb-3'>Cliente (tenant) *</h6>
                  <Input type='select' value={tenantId} onChange={e => setTenantId(e.target.value)}>
                    <option value=''>Selecione o cliente...</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Input>
                </CardBody></Card>
              )}

              {/* 1. Nome */}
              <Card><CardBody>
                <h6 className='fw-semibold mb-3'>Nome da campanha *</h6>
                <Input type='text' innerRef={campaignNameRef} value={name} onChange={e => setName(e.target.value)} placeholder='Ex: Prospecção Clínicas Palmas — Mai/26' />
              </CardBody></Card>

              {/* 1b. Segmento */}
              <Card><CardBody>
                <h6 className='fw-semibold mb-1'>Segmento do lead <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 12 }}>(opcional)</span></h6>
                <p style={{ fontSize: 12, color: '#6c757d', marginBottom: 10 }}>
                  Se preenchido, o agente <strong>já saberá</strong> o segmento e nunca perguntará. Se vazio, o agente qualifica no fluxo.
                </p>
                <Input
                  type='text'
                  value={segment}
                  onChange={e => setSegment(e.target.value)}
                  placeholder='Ex: mecânica, clínica odontológica, loja de roupas...'
                  maxLength={100}
                />
              </CardBody></Card>

              {/* 2. Agente */}
              <Card><CardBody>
                <div className='mb-3'>
                  <h6 className='fw-semibold mb-0'>Agente de IA responsável *</h6>
                </div>
                {agents.length === 0 ? (
                  <p className='text-muted small'>Nenhum agente cadastrado. Crie um para continuar.</p>
                ) : (
                  <div className='d-flex flex-column gap-2'>
                    {agents.map(a => {
                      const sel = prospectAgentId === a.id;
                      return (
                        <label key={a.id} onClick={() => setProspectAgentId(a.id)} className='rounded' style={{
                          cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '12px 16px',
                          border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
                          background: sel ? '#f0eeff' : '#fff', color: '#212529',
                        }}>
                          <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginRight: 12,
                            border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
                            background: sel ? '#7366ff' : '#fff',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {sel && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                          </span>
                          <div>
                            <p className='mb-0 fw-medium small' style={{ color: '#212529' }}>{a.name}</p>
                            {a.company_name && <p className='mb-0' style={{ fontSize: '0.72rem', color: '#6c757d' }}>{a.company_name}</p>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </CardBody></Card>

              {/* 3. Conexões */}
              <Card><CardBody>
                <h6 className='fw-semibold mb-3'>Conexões WhatsApp *</h6>
                {connections.length === 0 ? (
                  <p className='text-muted small'>Nenhuma instância conectada disponível.</p>
                ) : (
                  <div className='d-flex flex-column gap-2'>
                    {connections.map(c => {
                      const sel = selectedConns.includes(c.id);
                      return (
                        <label key={c.id} onClick={() => toggleConn(c.id)} className='rounded' style={{
                          cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '12px 16px',
                          border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
                          background: sel ? '#f0eeff' : '#fff', color: '#212529',
                        }}>
                          <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginRight: 12,
                            border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
                            background: sel ? '#7366ff' : '#fff',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {sel && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                          </span>
                          <div>
                            <p className='mb-0 fw-medium small'>{c.display_name}</p>
                            <p className='mb-0' style={{ fontSize: '0.75rem', color: '#6c757d' }}>{c.phone_number || c.instance_name}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </CardBody></Card>

              {/* 4. Listas */}
              <Card><CardBody>
                <h6 className='fw-semibold mb-3'>Listas de contatos *</h6>
                <Input type='text' value={listSearch} onChange={e => setListSearch(e.target.value)}
                  placeholder='Buscar lista...' className='mb-3' bsSize='sm' />
                {filteredLists.length === 0 ? (
                  <p className='text-muted small'>Nenhuma lista encontrada.</p>
                ) : (
                  <div className='d-flex flex-column gap-2' style={{ maxHeight: 240, overflowY: 'auto' }}>
                    {filteredLists.map(l => {
                      const sel = selectedLists.includes(l.id);
                      return (
                        <label key={l.id} onClick={() => toggleList(l.id)} className='rounded' style={{
                          cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '12px 16px',
                          border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
                          background: sel ? '#f0eeff' : '#fff', color: '#212529',
                        }}>
                          <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginRight: 12,
                            border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
                            background: sel ? '#7366ff' : '#fff',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {sel && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                          </span>
                          <p className='mb-0 fw-medium small flex-grow-1'>{l.name}</p>
                          <span style={{ fontSize: '0.75rem', color: '#6c757d', whiteSpace: 'nowrap', marginLeft: 8 }}>
                            {(l.total_contacts || 0).toLocaleString('pt-BR')} contatos
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {selectedLists.length > 0 && (
                  <p className='mt-3 mb-0 fw-medium text-primary small'>
                    Total: {totalContacts.toLocaleString('pt-BR')} contatos
                  </p>
                )}
              </CardBody></Card>

              {/* 5. Nota — Mensagens isca */}
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: '#f0eeff', borderRadius: 10, padding: '12px 16px',
                border: '1px solid #d6d0ff',
              }}>
                <svg style={{ flexShrink: 0, marginTop: 1 }} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#7366ff' strokeWidth='2'>
                  <circle cx='12' cy='12' r='10'/><line x1='12' y1='16' x2='12' y2='12'/><line x1='12' y1='8' x2='12.01' y2='8'/>
                </svg>
                <p style={{ margin: 0, fontSize: 13, color: '#5b4fcf', lineHeight: 1.5 }}>
                  As mensagens isca são configuradas em <strong>Configurações → Mensagens Isca</strong>.
                </p>
              </div>

              {/* 6a. Notificação de leads quentes */}
              <Card><CardBody>
                <div className='d-flex align-items-center justify-content-between mb-3'>
                  <h6 className='fw-semibold mb-0'>Notificação de leads quentes</h6>
                  <Button color='light' size='sm' onClick={() => setQuickMemberModal(true)}>
                    <Plus size={13} className='me-1' /> Novo membro
                  </Button>
                </div>
                {teamMembers.length === 0 ? (
                  <p className='text-muted small mb-0'>Cadastre membros do time para receber notificações.</p>
                ) : (
                  <div className='d-flex flex-column gap-2'>
                    {teamMembers.map(m => {
                      const sel = notifyMemberIds.includes(m.id);
                      return (
                        <label key={m.id} onClick={() => toggleNotify(m.id)} className='rounded' style={{
                          cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '10px 14px',
                          border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
                          background: sel ? '#f0eeff' : '#fff', color: '#212529',
                        }}>
                          <span style={{ width: 16, height: 16, borderRadius: 3, flexShrink: 0, marginRight: 10,
                            border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
                            background: sel ? '#7366ff' : '#fff',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {sel && <span style={{ width: 8, height: 8, borderRadius: 1, background: '#fff' }} />}
                          </span>
                          <span className='small fw-medium flex-grow-1'>{m.name}</span>
                          <Badge color={m.role === 'gerente' ? 'warning' : 'success'} style={{ fontSize: '11px' }}>
                            {m.role === 'gerente' ? 'Gerente' : 'Vendedor'}
                          </Badge>
                        </label>
                      );
                    })}
                  </div>
                )}
              </CardBody></Card>

              {/* 6b. Atribuição e Relatório */}
              <Card><CardBody>
                <h6 className='fw-semibold mb-3'>Atribuição e Relatório</h6>
                <div className='d-flex flex-column gap-3'>
                  <FormGroup className='mb-0'>
                    <Label className='small fw-medium'>Atribuição do lead</Label>
                    <div className='d-flex gap-2'>
                      {[{ id: 'round_robin', label: 'Rodízio automático' }, { id: 'fixed', label: 'Vendedor fixo' }].map(o => {
                        const sel = assignMode === o.id;
                        return (
                          <label key={o.id} onClick={() => setAssignMode(o.id)} className='rounded' style={{
                            cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 14px',
                            border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
                            background: sel ? '#f0eeff' : '#fff', color: '#212529',
                          }}>
                            <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginRight: 8,
                              border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
                              background: sel ? '#7366ff' : '#fff',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              {sel && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                            </span>
                            <span className='small'>{o.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </FormGroup>
                  {assignMode === 'fixed' && (
                    <FormGroup className='mb-0'>
                      <Label className='small fw-medium'>Vendedor fixo</Label>
                      <Input type='select' bsSize='sm' value={fixedMemberId} onChange={e => setFixedMemberId(e.target.value)}>
                        <option value=''>Selecione...</option>
                        {teamMembers.filter(m => m.role === 'vendedor').map(m => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  )}
                  <FormGroup className='mb-0'>
                    <Label className='small fw-medium'>Relatório diário</Label>
                    <div className='d-flex gap-2 flex-wrap'>
                      {REPORT_MODES.map(o => {
                        const sel = reportMode === o.id;
                        return (
                          <label key={o.id} onClick={() => setReportMode(o.id)} className='rounded' style={{
                            cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 14px',
                            border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
                            background: sel ? '#f0eeff' : '#fff', color: '#212529',
                          }}>
                            <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginRight: 8,
                              border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
                              background: sel ? '#7366ff' : '#fff',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              {sel && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                            </span>
                            <span className='small'>{o.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </FormGroup>
                </div>
              </CardBody></Card>

              {tab0Error && <Alert color='danger' toggle={() => setTab0Error('')}>{tab0Error}</Alert>}
              <div className='d-flex justify-content-end' style={{ marginBottom: 40 }}>
                <Button color='primary' disabled={saving} onClick={saveTab0}>
                  {saving ? 'Salvando...' : 'Salvar e continuar →'}
                </Button>
              </div>
            </div>
          </TabPane>

          {/* ════════════════════════════════════════════════════════════
              ABA 1 — MÍDIAS
          ════════════════════════════════════════════════════════════ */}
          <TabPane tabId='1'>
            {!campaignId ? (
              <div className='text-center py-5 text-muted'>
                <Database size={48} className='mb-3' />
                <h6>Salve as Configurações primeiro para gerenciar mídias.</h6>
              </div>
            ) : (
              <>
                {/* Cards de resumo — full width */}
                <Row className='mb-2'>
                  {MEDIA_SUMMARY.map(s => (
                    <Col xl='3' md='6' sm='6' key={s.key} className='mb-4'>
                      <Card className='widget-1'>
                        <CardBody>
                          <div className='widget-content'>
                            <div className={`widget-round ${s.colorClass}`}>
                              <div className='bg-round' style={{
                                background: s.gradient, overflow: 'hidden', width: 54, height: 54,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              }}>
                                <s.Icon color='#fff' strokeWidth={1.5} style={{ width: 24, height: 24, flexShrink: 0 }} />
                              </div>
                            </div>
                            <div>
                              <h4 className='mb-1 f-w-600'><CountUp end={mediaCounts[s.key]} duration={1.5} /></h4>
                              <span className='f-light f-12'>{s.label}</span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* Conteúdo centralizado */}
                <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Header */}
                <div className='d-flex justify-content-end'>
                  <button
                    onClick={openUploadModal}
                    style={{
                      background: '#7366ff', color: '#fff', border: 'none', borderRadius: 8,
                      padding: '7px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <Plus size={14} /> Upload de mídia
                  </button>
                </div>

                {/* Filtros + grid */}
                <Card>
                  <CardBody>
                    <div className='d-flex gap-3 mb-4 flex-wrap align-items-center'>
                      <div className='d-flex gap-1'>
                        {[['', 'Todos'], ['video', 'Vídeo'], ['photo', 'Foto'], ['audio', 'Áudio']].map(([v, l]) => (
                          <button key={v} onClick={() => setMediaTabFilter(v)} style={{
                            padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            cursor: 'pointer', border: 'none',
                            background: mediaTabFilter === v ? '#7366ff' : '#f4f4f4',
                            color:      mediaTabFilter === v ? '#fff'    : '#6c757d',
                            transition: 'all .15s',
                          }}>{l}</button>
                        ))}
                      </div>
                      <InputGroup style={{ maxWidth: 260 }}>
                        <span className='input-group-text bg-transparent'><Search size={13} /></span>
                        <Input placeholder='Buscar mídia...' bsSize='sm'
                          value={mediaTabSearch} onChange={e => setMediaTabSearch(e.target.value)} />
                      </InputGroup>
                    </div>

                    {mediaLoading ? (
                      <div className='text-center py-5 text-muted'>Carregando...</div>
                    ) : filteredTabMedias.length === 0 ? (
                      <div className='text-center py-5'>
                        <Database size={48} className='mb-3 text-muted' />
                        <h6 className='text-muted'>
                          {mediaTabSearch || mediaTabFilter
                            ? 'Nenhuma mídia encontrada'
                            : 'Nenhuma mídia nesta campanha. Faça o primeiro upload!'}
                        </h6>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                        {filteredTabMedias.map(m => {
                          const cfg     = MEDIA_CFG[m.type] || MEDIA_CFG.video;
                          const isCopied = mediaCopied === m.url;
                          const btnBase  = {
                            width: '100%', padding: '6px 0', borderRadius: 6, fontSize: 11,
                            fontWeight: 600, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                            transition: 'all .15s',
                          };
                          return (
                            <div key={m.id} style={{
                              border: '0.5px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff',
                              display: 'flex', flexDirection: 'column', gap: 10, transition: 'box-shadow .15s',
                            }}
                              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(115,102,255,.12)'}
                              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                            >
                              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 4 }}>
                                <div style={{ width: 64, height: 64, borderRadius: 16, background: cfg.color + '18',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <cfg.Icon size={28} color={cfg.color} strokeWidth={1.5} />
                                </div>
                              </div>
                              <div style={{ fontWeight: 500, fontSize: 13, textAlign: 'center',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {m.name}
                              </div>
                              <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                                {cfg.label} · {formatSize(m.size_bytes)}
                              </div>
                              <div style={{ fontSize: 11, color: '#b0bac9', textAlign: 'center' }}>
                                {new Date(m.created_at).toLocaleDateString('pt-BR')}
                              </div>
                              <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <button onClick={() => setMediaPreview(m)}
                                  style={{ ...btnBase, border: `1px solid ${cfg.color}44`, background: cfg.color + '10', color: cfg.color }}>
                                  <Play size={11} /> Preview
                                </button>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button onClick={() => copyMediaUrl(m.url)} title='Copiar URL' style={{
                                    ...btnBase, flex: 1,
                                    border: `1px solid ${isCopied ? '#54ba4a' : '#e5e7eb'}`,
                                    background: isCopied ? '#54ba4a18' : '#f8f9fa',
                                    color: isCopied ? '#54ba4a' : '#495057',
                                  }}>
                                    {isCopied ? <CheckCircle size={12} /> : <Copy size={12} />}
                                    {isCopied ? 'Copiado!' : 'Copiar URL'}
                                  </button>
                                  <button onClick={() => handleMediaDelete(m)} title='Remover' style={{
                                    padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                                    border: '1px solid #ffe0e0', background: '#fff0f0', color: '#f44336',
                                  }}>
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {!mediaLoading && (
                      <div className='mt-2'>
                        <small className='text-muted'>{filteredTabMedias.length} mídia{filteredTabMedias.length !== 1 ? 's' : ''}</small>
                      </div>
                    )}
                  </CardBody>
                </Card>

                <div className='d-flex justify-content-between' style={{ marginBottom: 40 }}>
                  <Button color='light' onClick={() => setTab('0')}>← Voltar</Button>
                  <Button color='primary' onClick={() => setTab('2')}>Continuar →</Button>
                </div>
                </div>{/* fim container centralizado */}
              </>
            )}

            {/* Preview overlay */}
            {mediaPreview && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }} onClick={() => setMediaPreview(null)}>
                <div style={{
                  position: 'relative', background: '#1e1e2e', borderRadius: 14, padding: '20px 20px 16px',
                  maxWidth: '90vw', minWidth: 320, maxHeight: '92vh', overflow: 'auto',
                }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => setMediaPreview(null)} style={{
                    position: 'absolute', top: 12, right: 12, width: 32, height: 32,
                    borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,.15)',
                    color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <X size={15} />
                  </button>
                  <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 14, marginBottom: 14, paddingRight: 40, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {(() => { const c = MEDIA_CFG[mediaPreview.type] || MEDIA_CFG.video; return <c.Icon size={14} color={c.color} strokeWidth={1.5} />; })()}
                    {mediaPreview.name}
                  </div>
                  {mediaPreview.type === 'video' && (
                    <video controls autoPlay style={{ width: '100%', maxWidth: '720px', maxHeight: '60vh', display: 'block', borderRadius: 8, backgroundColor: '#000' }} src={mediaPreview.url}>
                      Seu browser não suporta vídeo.
                    </video>
                  )}
                  {mediaPreview.type === 'photo' && (
                    <img src={mediaPreview.url} alt={mediaPreview.name} style={{ maxWidth: '80vw', maxHeight: '72vh', borderRadius: 8, display: 'block', margin: '0 auto' }} />
                  )}
                  {mediaPreview.type === 'audio' && (
                    <div style={{ padding: '12px 0' }}>
                      <audio controls autoPlay style={{ width: '100%', minWidth: 280 }} src={mediaPreview.url} />
                    </div>
                  )}
                  <div style={{ color: '#64748b', fontSize: 11, marginTop: 12, textAlign: 'center' }}>
                    {formatSize(mediaPreview.size_bytes)} · {new Date(mediaPreview.created_at).toLocaleDateString('pt-BR')}
                  </div>
                </div>
              </div>
            )}
          </TabPane>

          {/* ════════════════════════════════════════════════════════════
              ABA 2 — FLUXO DE ABORDAGEM
          ════════════════════════════════════════════════════════════ */}
          <TabPane tabId='2'>
            {tab === '2' && (
            <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 16px', paddingBottom: 60 }}>

              {/* Nó central */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg, #7366ff, #563ee0)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 15px rgba(115,102,255,0.35)' }}>
                  <Target size={24} color='#fff' />
                </div>
                <div style={{ width: 2, height: 16, background: '#e2e8f0', margin: '4px 0' }} />
                <Card className='shadow-sm mb-0' style={{ borderRadius: 12, border: 'none', minWidth: 200, textAlign: 'center' }}>
                  <CardBody style={{ padding: '10px 20px' }}>
                    <p style={{ fontWeight: 600, fontSize: 14, color: '#2c323f', margin: 0 }}>{name || 'Campanha'}</p>
                    <p style={{ fontSize: 12, color: '#aaa', margin: 0 }}>
                      {agents.find(a => a.id === prospectAgentId)?.name || 'Sem agente'} · {flowSteps.length} etapa{flowSteps.length !== 1 ? 's' : ''}
                    </p>
                  </CardBody>
                </Card>
              </div>

              {/* Steps — accordion */}
              {flowSteps.length === 0 && (
                <div className='text-center py-4 text-muted' style={{ fontSize: 13 }}>
                  Nenhuma etapa ainda. Clique em "Adicionar etapa" abaixo.
                </div>
              )}
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId='flow-steps'>
                  {(droppableProvided) => (
                    <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                      {flowSteps.map((step, index) => {
                        const isExpanded = !!openSteps[step._uid];
                        const TYPE_META = {
                          isca:  { bg: '#fff8e1', border: '#f8a800', color: '#f8a800', label: 'Isca'  },
                          text:  { bg: '#f0eeff', border: '#7366ff', color: '#7366ff', label: 'Texto' },
                          media: { bg: '#e8f5e9', border: '#51bb25', color: '#51bb25', label: 'Mídia' },
                        };
                        const tc = TYPE_META[step.type] || TYPE_META.text;
                        const mediaCfg = step.media_type ? (MEDIA_CFG[step.media_type] || MEDIA_CFG.video) : null;
                        return (
                          <Draggable key={step._uid} draggableId={step._uid} index={index} isDragDisabled={step.type === 'isca'}>
                            {(draggableProvided) => (
                              <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                                {/* Conector */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '4px 0' }}>
                                  <div style={{ width: 2, height: 20, background: '#e2e8f0' }} />
                                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#7366ff', opacity: 0.4 }} />
                                  <div style={{ width: 2, height: 20, background: '#e2e8f0' }} />
                                </div>

                                <Card style={{
                                  borderRadius: 14,
                                  border: `1.5px solid ${isExpanded ? tc.border : '#e5e7eb'}`,
                                  boxShadow: isExpanded ? `0 4px 20px ${tc.border}22` : '0 2px 10px rgba(0,0,0,0.06)',
                                  transition: 'all .2s', overflow: 'hidden',
                                }}>
                                  {/* Header */}
                                  <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', background: isExpanded ? tc.bg : '#fff' }}
                                    onClick={() => setOpenSteps(prev => ({ ...prev, [step._uid]: !prev[step._uid] }))}>
                                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: tc.color + '20', color: tc.color, fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                      {index + 1}
                                    </div>
                                    <span style={{ background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`, borderRadius: 20, fontSize: 10, padding: '2px 8px', fontWeight: 600, flexShrink: 0 }}>
                                      {tc.label}
                                    </span>
                                    <span style={{ fontWeight: 600, fontSize: 14, color: '#2c323f', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {step.title || `Etapa ${index + 1}`}
                                    </span>
                                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                                      <span
                                        {...draggableProvided.dragHandleProps}
                                        title='Arrastar para reordenar'
                                        style={{ opacity: step.type === 'isca' ? 0 : 1, cursor: 'grab', padding: '3px 7px', fontSize: 16, color: '#94a3b8', display: 'flex', alignItems: 'center', outline: 'none', userSelect: 'none' }}
                                      >⠿</span>
                                      <button disabled={index === 0} onClick={() => moveStep(index, -1)}
                                        style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 7px', cursor: index === 0 ? 'not-allowed' : 'pointer', opacity: index === 0 ? 0.3 : 1, fontSize: 12, lineHeight: 1 }}>↑</button>
                                      <button disabled={index === flowSteps.length - 1} onClick={() => moveStep(index, 1)}
                                        style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 7px', cursor: index === flowSteps.length - 1 ? 'not-allowed' : 'pointer', opacity: index === flowSteps.length - 1 ? 0.3 : 1, fontSize: 12, lineHeight: 1 }}>↓</button>
                                      <button onClick={() => openEditModal(index)}
                                        style={{ background: 'none', border: '1px solid #7366ff44', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#7366ff', display: 'flex', alignItems: 'center' }}>
                                        <Edit2 size={12} />
                                      </button>
                                      <button onClick={() => removeStep(index)}
                                        style={{ background: 'none', border: '1px solid #dc354544', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#dc3545', display: 'flex', alignItems: 'center' }}>
                                        <Trash2 size={12} />
                                      </button>
                                      {isExpanded ? <ChevronUp size={16} color='#94a3b8' style={{ marginLeft: 2 }} /> : <ChevronDown size={16} color='#94a3b8' style={{ marginLeft: 2 }} />}
                                    </div>
                                  </div>

                                  {/* Expanded body */}
                                  {isExpanded && (
                                    <div style={{ padding: '0 18px 18px', borderTop: `1px solid ${tc.border}22` }}>
                                      <div style={{ marginTop: 14 }}>
                                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
                                          Instrução para o agente
                                        </label>
                                        <textarea
                                          value={step.instruction || ''}
                                          onChange={e => updateStep(index, 'instruction', e.target.value)}
                                          rows={4}
                                          placeholder='Descreva o que o agente deve fazer nesta etapa...'
                                          style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', resize: 'vertical', lineHeight: 1.6, outline: 'none', fontFamily: 'inherit' }}
                                        />
                                      </div>
                                      <div style={{ marginTop: 12 }}>
                                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
                                          Exemplo de resposta
                                        </label>
                                        <textarea
                                          value={step.example || ''}
                                          onChange={e => updateStep(index, 'example', e.target.value)}
                                          rows={4}
                                          placeholder='"Exemplo de como o agente deve responder..."'
                                          style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#374151', resize: 'vertical', lineHeight: 1.6, outline: 'none', background: '#fafbfc', fontFamily: 'inherit' }}
                                        />
                                      </div>
                                      {step.type === 'media' && (
                                        <div style={{ marginTop: 12 }}>
                                          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
                                            Mídia vinculada
                                          </label>
                                          {step.media_id ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8f9fa', borderRadius: 8, padding: '10px 14px' }}>
                                              {mediaCfg ? <mediaCfg.Icon size={18} color={mediaCfg.color} strokeWidth={1.5} /> : <Database size={18} color='#94a3b8' />}
                                              <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{step.media_name || 'Mídia'}</span>
                                              <button onClick={() => openEditModal(index)}
                                                style={{ background: 'none', border: '1px solid #dee2e6', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 11, color: '#6c757d' }}>Trocar</button>
                                            </div>
                                          ) : (
                                            <button onClick={() => openEditModal(index)}
                                              style={{ width: '100%', padding: '10px', borderRadius: 8, border: '2px dashed #51bb25', background: 'transparent', color: '#51bb25', fontWeight: 500, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                              <Plus size={14} /> Selecionar mídia
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {/* Adicionar etapa */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '20px 0' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '4px 0' }}>
                  <div style={{ width: 2, height: 20, background: '#e2e8f0' }} />
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#94a3b8', opacity: 0.4 }} />
                  <div style={{ width: 2, height: 20, background: '#e2e8f0' }} />
                </div>
                <button onClick={openAddModal} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', borderRadius: 25, border: '2px dashed #7366ff', background: 'transparent', color: '#7366ff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  <Plus size={15} /> Adicionar etapa
                </button>
              </div>

              {/* Checkbox: Desligar agente após última etapa do fluxo */}
              <div className='mb-3'>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input type='checkbox' checked={disableAgentAfterFlow}
                    onChange={e => setDisableAgentAfterFlow(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: '#dc3545', cursor: 'pointer' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#dc3545' }}>Desligar agente após última etapa do fluxo.</span>
                </label>
              </div>

              {tab2Error && <Alert color='danger' toggle={() => setTab2Error('')} className='mt-3'>{tab2Error}</Alert>}
              <div className='d-flex justify-content-between mt-4' style={{ marginBottom: 40 }}>
                <Button color='light' onClick={() => setTab('1')}>← Voltar</Button>
                <Button color='primary' onClick={saveTab2}>Salvar e continuar →</Button>
              </div>

            </div>
            )}
          </TabPane>

          {/* ════════════════════════════════════════════════════════════
              ABA 3 — DISPARO EM MASSA
          ════════════════════════════════════════════════════════════ */}
          <TabPane tabId='3'>
            {/* Banner estimativa — full width, sticky */}
            {totalContacts > 0 && daysEst !== '—' && (
              <div className='alert alert-warning mb-3' style={{ color: '#856404', position: 'sticky', top: 0, zIndex: 100 }}>
                Estimativa: <strong>{daysEst} dia{daysEst !== 1 ? 's' : ''}</strong> para completar {totalContacts.toLocaleString('pt-BR')} disparos com os horários configurados abaixo.
              </div>
            )}
            <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 20, paddingBottom: 60 }}>

              <Card><CardBody>
                <h6 className='fw-semibold mb-3'>Horários por dia da semana</h6>
                <div className='d-flex flex-column gap-2'>
                  {schedule.map(d => (
                    <div key={d.day_of_week} className='d-flex align-items-center gap-3'>
                      <div className='d-flex align-items-center gap-2' style={{ width: 76 }}>
                        <div className='form-check form-switch mb-0'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            role='switch'
                            checked={d.enabled}
                            onChange={e => updateDay(d.day_of_week, 'enabled', e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                        <span className={d.enabled ? 'fw-medium small' : 'text-muted small'}>{DAYS[d.day_of_week]}</span>
                      </div>
                      {d.enabled ? (
                        <div className='d-flex align-items-center gap-2'>
                          <Input type='time' bsSize='sm' value={d.start_time} onChange={e => updateDay(d.day_of_week, 'start_time', e.target.value)} />
                          <span className='text-muted small'>até</span>
                          <Input type='time' bsSize='sm' value={d.end_time} onChange={e => updateDay(d.day_of_week, 'end_time', e.target.value)} />
                        </div>
                      ) : <span className='text-muted' style={{ fontSize: '0.75rem' }}>Inativo</span>}
                    </div>
                  ))}
                </div>
              </CardBody></Card>

              <Card><CardBody>
                <h6 className='fw-semibold mb-3'>Intervalo entre mensagens</h6>
                <div className='row g-3'>
                  <div className='col-md-6'><FormGroup>
                    <Label className='small fw-medium'>Mínimo (segundos)</Label>
                    <Input type='number' bsSize='sm' value={intervalMin} min={5} max={300} onChange={e => setIntMin(Number(e.target.value))} />
                  </FormGroup></div>
                  <div className='col-md-6'><FormGroup>
                    <Label className='small fw-medium'>Máximo (segundos)</Label>
                    <Input type='number' bsSize='sm' value={intervalMax} min={5} max={300} onChange={e => setIntMax(Number(e.target.value))} />
                  </FormGroup></div>
                </div>
              </CardBody></Card>

              <Card><CardBody>
                <h6 className='fw-semibold mb-3'>Pausa entre lotes (anti-banimento)</h6>
                <div className='row g-3'>
                  <div className='col-md-6'><FormGroup>
                    <Label className='small fw-medium'>Disparos por lote (mín)</Label>
                    <Input type='number' bsSize='sm' value={batchMin} min={1} onChange={e => setBatchMin(Number(e.target.value))} />
                  </FormGroup></div>
                  <div className='col-md-6'><FormGroup>
                    <Label className='small fw-medium'>Disparos por lote (máx)</Label>
                    <Input type='number' bsSize='sm' value={batchMax} min={1} onChange={e => setBatchMax(Number(e.target.value))} />
                  </FormGroup></div>
                  <div className='col-md-6'><FormGroup>
                    <Label className='small fw-medium'>Pausa mín (minutos)</Label>
                    <Input type='number' bsSize='sm' value={pauseMin} min={1} onChange={e => setPauseMin(Number(e.target.value))} />
                  </FormGroup></div>
                  <div className='col-md-6'><FormGroup>
                    <Label className='small fw-medium'>Pausa máx (minutos)</Label>
                    <Input type='number' bsSize='sm' value={pauseMax} min={1} onChange={e => setPauseMax(Number(e.target.value))} />
                  </FormGroup></div>
                </div>
              </CardBody></Card>

              <div style={{ display:'flex', gap:'12px', justifyContent:'space-between', alignItems:'center', marginTop:'24px', marginBottom:'40px' }}>
                <button style={{ border:'1px solid #dee2e6', background:'none', borderRadius:'8px', padding:'8px 20px', cursor:'pointer' }}
                  onClick={() => setTab('2')}>← Voltar</button>
                <div style={{ display:'flex', gap:'12px' }}>
                  <button style={{ border:'1px solid #6c757d', color:'#6c757d', background:'none', borderRadius:'8px', padding:'8px 20px', cursor:'pointer', fontWeight:500 }}
                    onClick={handleSaveDraft}>💾 Salvar</button>
                  <button style={{ border:'1px solid #7366ff', color:'#7366ff', background:'none', borderRadius:'8px', padding:'8px 20px', cursor:'pointer', fontWeight:500 }}
                    onClick={() => setScheduleModal(true)}>📅 Agendar</button>
                  <button style={{ background:'#54ba4a', color:'#fff', border:'none', borderRadius:'8px', padding:'8px 20px', cursor:'pointer', fontWeight:500 }}
                    disabled={launching} onClick={() => launch('running')}>
                    {launching ? 'Iniciando...' : '▶ Disparar agora'}
                  </button>
                </div>
              </div>
            </div>
          </TabPane>

        </TabContent>
      </Container>

      {/* ── Modal rápido: agente ─────────────────────────────────────────── */}
      <Modal isOpen={quickAgentModal} toggle={() => setQuickAgentModal(false)}>
        <ModalHeader toggle={() => setQuickAgentModal(false)}>Novo agente</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className='small fw-medium'>Nome *</Label>
            <Input value={quickAgentForm.name} onChange={e => setQuickAgentForm(p => ({ ...p, name: e.target.value }))} placeholder='Ex: Nayara' />
          </FormGroup>
          <FormGroup>
            <Label className='small fw-medium'>Empresa</Label>
            <Input value={quickAgentForm.company_name} onChange={e => setQuickAgentForm(p => ({ ...p, company_name: e.target.value }))} placeholder='Ex: Topo 360' />
          </FormGroup>
          <FormGroup>
            <Label className='small fw-medium'>Cidade</Label>
            <Input value={quickAgentForm.city} onChange={e => setQuickAgentForm(p => ({ ...p, city: e.target.value }))} placeholder='Ex: Palmas - TO' />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setQuickAgentModal(false)}>Cancelar</Button>
          <Button color='primary' disabled={!quickAgentForm.name.trim()} onClick={handleQuickAgent}>Criar agente</Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal rápido: membro ─────────────────────────────────────────── */}
      <Modal isOpen={quickMemberModal} toggle={() => setQuickMemberModal(false)}>
        <ModalHeader toggle={() => setQuickMemberModal(false)}>Novo membro do time</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className='small fw-medium'>Nome *</Label>
            <Input value={quickMemberForm.name} onChange={e => setQuickMemberForm(p => ({ ...p, name: e.target.value }))} placeholder='Nome completo' />
          </FormGroup>
          <FormGroup>
            <Label className='small fw-medium'>WhatsApp * <span className='text-muted fw-normal'>(55DDD...)</span></Label>
            <Input value={quickMemberForm.phone} onChange={e => setQuickMemberForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '') }))} placeholder='5563999990000' maxLength={15} />
          </FormGroup>
          <FormGroup>
            <Label className='small fw-medium'>Cargo</Label>
            <div className='d-flex gap-2'>
              {[{ id: 'vendedor', label: 'Vendedor' }, { id: 'gerente', label: 'Gerente' }].map(o => {
                const sel = quickMemberForm.role === o.id;
                return (
                  <label key={o.id} onClick={() => setQuickMemberForm(p => ({ ...p, role: o.id }))} className='rounded' style={{
                    cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 14px',
                    border: sel ? '2px solid #7366ff' : '1px solid #dee2e6',
                    background: sel ? '#f0eeff' : '#fff', color: '#212529',
                  }}>
                    <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginRight: 8,
                      border: sel ? '2px solid #7366ff' : '2px solid #dee2e6',
                      background: sel ? '#7366ff' : '#fff',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      {sel && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                    </span>
                    <span className='small'>{o.label}</span>
                  </label>
                );
              })}
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setQuickMemberModal(false)}>Cancelar</Button>
          <Button color='primary' disabled={!quickMemberForm.name.trim() || !quickMemberForm.phone.trim()} onClick={handleQuickMember}>Criar membro</Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal: add / edit step do fluxo ─────────────────────────────── */}
      <Modal isOpen={!!flowModal} toggle={() => setFlowModal(null)} centered size='lg'>
        <ModalHeader toggle={() => setFlowModal(null)}>
          {flowModal === 'add' ? 'Nova etapa' : 'Editar etapa'}
        </ModalHeader>
        <ModalBody>
          {/* Título */}
          <div className='mb-3'>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Título da etapa *</label>
            <Input bsSize='sm' value={flowForm.title || ''} onChange={e => setFlowForm(f => ({ ...f, title: e.target.value }))} placeholder='Ex: Pergunta de Dor' />
          </div>

          {/* Tipo */}
          <div className='mb-3'>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>Tipo</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['isca', 'Isca', '#f8a800'], ['text', 'Texto', '#7366ff'], ['media', 'Mídia', '#51bb25']]
                .filter(([k]) => k !== 'isca' || flowModal !== 'add' || !flowSteps.some(s => s.type === 'isca'))
                .map(([k, l, c]) => (
                <button key={k} onClick={() => setFlowForm(f => ({ ...f, type: k }))} style={{
                  flex: 1, padding: '8px', borderRadius: 8, border: `2px solid ${flowForm.type === k ? c : '#e5e7eb'}`,
                  background: flowForm.type === k ? c + '12' : '#fafafa', color: flowForm.type === k ? c : '#6c757d',
                  fontWeight: 600, fontSize: 12, cursor: 'pointer', transition: 'all .15s',
                }}>{l}</button>
              ))}
            </div>
          </div>

          {/* Instrução + Exemplo (isca / text) */}
          {(flowForm.type === 'isca' || flowForm.type === 'text' || !flowForm.type) && (
            <>
              <div className='mb-3'>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Instrução para o agente</label>
                <textarea value={flowForm.instruction || ''} onChange={e => setFlowForm(f => ({ ...f, instruction: e.target.value }))}
                  rows={4} placeholder='Descreva o que o agente deve fazer nesta etapa...'
                  style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div className='mb-3'>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Exemplo de resposta</label>
                <textarea value={flowForm.example || ''} onChange={e => setFlowForm(f => ({ ...f, example: e.target.value }))}
                  rows={2} placeholder='"Exemplo de como o agente deve responder..."'
                  style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, resize: 'vertical', fontStyle: 'italic', fontFamily: 'inherit', background: '#fafbfc' }} />
              </div>
            </>
          )}

          {/* Seletor de mídia + opções adicionais */}
          {flowForm.type === 'media' && (
            <>
              <div className='mb-3'>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>Selecionar mídia</label>
                {campaignMedias.length === 0 ? (
                  <p className='text-muted text-center py-3 small'>Nenhuma mídia na campanha. Adicione mídias na aba Mídias.</p>
                ) : (
                  <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                      {campaignMedias.map(m => {
                        const cfg = MEDIA_CFG[m.type] || MEDIA_CFG.video;
                        const sel = flowForm.media_id === m.id;
                        return (
                          <div key={m.id} onClick={() => setFlowForm(f => ({ ...f, media_id: m.id, media_name: m.name, media_type: m.type }))}
                            style={{ border: `2px solid ${sel ? cfg.color : '#e5e7eb'}`, borderRadius: 10, padding: 10, textAlign: 'center', cursor: 'pointer', background: sel ? cfg.bg : '#fff', transition: 'all .15s' }}>
                            <cfg.Icon size={22} color={cfg.color} strokeWidth={1.5} />
                            <div style={{ fontSize: 11, fontWeight: 500, marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8' }}>{cfg.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Checkbox: Enviar sem permissão */}
              <div className='mb-3'>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input type='checkbox' checked={!!flowForm.send_without_permission}
                    onChange={e => setFlowForm(f => ({ ...f, send_without_permission: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: '#7366ff', cursor: 'pointer' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Enviar mídia sem pedir permissão ao lead</span>
                </label>
                {flowForm.send_without_permission && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, fontSize: 12, color: '#b45309' }}>
                    ⚠️ A mídia será disparada automaticamente, sem esperar resposta do lead. Os campos abaixo ficam desativados.
                  </div>
                )}
              </div>

              {/* Instrução + Exemplo para tipo mídia */}
              <div style={{ opacity: flowForm.send_without_permission ? 0.4 : 1, pointerEvents: flowForm.send_without_permission ? 'none' : 'auto', transition: 'opacity .2s' }}>
                <div className='mb-3'>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Instrução para o agente</label>
                  <textarea value={flowForm.instruction || ''} onChange={e => setFlowForm(f => ({ ...f, instruction: e.target.value }))}
                    rows={3} placeholder='Ex: Após enviar o vídeo, pergunte o que achou...'
                    style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }} />
                </div>
                <div className='mb-3'>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>Exemplo de resposta</label>
                  <textarea value={flowForm.example || ''} onChange={e => setFlowForm(f => ({ ...f, example: e.target.value }))}
                    rows={2} placeholder='"Que tal? Faz sentido para o seu negócio?"'
                    style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, resize: 'vertical', fontStyle: 'italic', fontFamily: 'inherit', background: '#fafbfc' }} />
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color='light' onClick={() => setFlowModal(null)}>Cancelar</Button>
          <Button color='primary' disabled={!flowForm.title?.trim()} onClick={saveFlowModal}>
            {flowModal === 'add' ? 'Adicionar' : 'Salvar'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Modal de Upload de Mídia ─────────────────────────────────────── */}
      <Modal isOpen={showUploadModal} toggle={() => !mediaUploading && setShowUploadModal(false)} centered size='md'>
        <ModalHeader toggle={() => !mediaUploading && setShowUploadModal(false)}>
          <Upload size={16} className='me-2' style={{ color: '#7366ff', verticalAlign: 'middle' }} />
          Upload de mídia
        </ModalHeader>
        <ModalBody>
          <div className='mb-3'>
            <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
              Nome / ID da mídia *
            </label>
            <Input bsSize='sm' placeholder='ex: video-apresentacao-produto'
              value={mediaUploadForm.name}
              onChange={e => setMediaUploadForm(f => ({ ...f, name: e.target.value }))}
              invalid={!!(mediaUploadForm.name && !mediaSlugValid)}
              disabled={mediaUploading}
            />
            {mediaUploadForm.name ? (
              <small style={{ fontSize: 11, color: mediaSlugValid ? '#54ba4a' : '#f44336', marginTop: 3, display: 'block' }}>
                Slug: <strong>{mediaSlug || '—'}</strong>
                {!mediaSlugValid && ' · inválido — apenas a-z 0-9 _ -'}
              </small>
            ) : (
              <small className='text-muted' style={{ fontSize: 11 }}>Letras minúsculas, números, - e _ apenas.</small>
            )}
          </div>

          <div className='mb-3'>
            <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, display: 'block' }}>Tipo *</label>
            <div className='d-flex gap-2'>
              {Object.entries(MEDIA_CFG).map(([k, cfg]) => (
                <button key={k}
                  onClick={() => !mediaUploading && setMediaUploadForm(f => ({ ...f, type: k, file: null }))}
                  style={{
                    flex: 1, padding: '10px 8px', borderRadius: 8,
                    border: `2px solid ${mediaUploadForm.type === k ? cfg.color : '#e5e7eb'}`,
                    background: mediaUploadForm.type === k ? cfg.color + '12' : '#fafafa',
                    color: mediaUploadForm.type === k ? cfg.color : '#6c757d',
                    fontWeight: 600, fontSize: 12, cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
                    transition: 'all .15s',
                  }}
                >
                  <cfg.Icon size={18} strokeWidth={1.5} />
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          <div
            onDragOver={e => { e.preventDefault(); setMediaDragOver(true); }}
            onDragLeave={() => setMediaDragOver(false)}
            onDrop={e => { e.preventDefault(); setMediaDragOver(false); const f = e.dataTransfer.files[0]; if (f) onMediaFileChange(f); }}
            onClick={() => !mediaUploading && mediaFileRef.current?.click()}
            style={{
              border: `2px dashed ${mediaDragOver ? '#7366ff' : '#d1d5db'}`,
              borderRadius: 10, padding: '28px 16px', textAlign: 'center',
              background: mediaDragOver ? '#f0eeff' : '#fafafa',
              cursor: mediaUploading ? 'not-allowed' : 'pointer', transition: 'all .15s',
            }}
          >
            {mediaUploadForm.file ? (
              <>
                <CheckCircle size={28} color='#54ba4a' style={{ marginBottom: 6 }} />
                <div style={{ fontWeight: 600, fontSize: 13 }}>{mediaUploadForm.file.name}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{formatSize(mediaUploadForm.file.size)}</div>
              </>
            ) : (
              <>
                <Upload size={28} color='#9ca3af' style={{ marginBottom: 6 }} />
                <div style={{ fontWeight: 600, fontSize: 13, color: '#6c757d' }}>
                  Arraste o arquivo ou clique para selecionar
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                  {(MEDIA_CFG[mediaUploadForm.type] || MEDIA_CFG.video).accept.replace(/\./g, '').toUpperCase().split(',').join(', ')}
                  {' '}· máx {(MEDIA_CFG[mediaUploadForm.type] || MEDIA_CFG.video).maxMB}MB
                </div>
              </>
            )}
            <input ref={mediaFileRef} type='file'
              accept={(MEDIA_CFG[mediaUploadForm.type] || MEDIA_CFG.video).accept}
              style={{ display: 'none' }}
              onChange={e => onMediaFileChange(e.target.files[0])}
              disabled={mediaUploading}
            />
          </div>

          {mediaUploading && (
            <div className='mt-3'>
              <div className='d-flex justify-content-between mb-1'>
                <small className='text-muted'>Enviando...</small>
                <small className='text-muted'>{mediaUploadPct}%</small>
              </div>
              <div style={{ height: 6, background: '#f1f1f1', borderRadius: 10 }}>
                <div style={{ height: '100%', width: `${mediaUploadPct}%`, background: '#7366ff', borderRadius: 10, transition: 'width .2s' }} />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button onClick={() => setShowUploadModal(false)} disabled={mediaUploading} style={{
            padding: '7px 18px', borderRadius: 8, border: '1px solid #e5e7eb',
            background: '#f8f9fa', color: '#495057', fontWeight: 600, fontSize: 13,
            cursor: mediaUploading ? 'not-allowed' : 'pointer',
          }}>Cancelar</button>
          <button onClick={handleMediaUpload} disabled={!canSubmitUpload} style={{
            padding: '7px 20px', borderRadius: 8, border: 'none',
            background: canSubmitUpload ? '#7366ff' : '#c4b5fd',
            color: '#fff', fontWeight: 600, fontSize: 13,
            cursor: canSubmitUpload ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {mediaUploading ? 'Enviando...' : <><Upload size={13} /> Enviar e salvar</>}
          </button>
        </ModalFooter>
      </Modal>

      {/* ── Modal: agendar campanha ──────────────────────────────────────── */}
      <Modal isOpen={scheduleModal} toggle={() => setScheduleModal(false)} centered>
        <ModalHeader toggle={() => setScheduleModal(false)}>Agendar campanha</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Data e hora de início</Label>
            <Input type='datetime-local' value={scheduleDate}
              onChange={e => setScheduleDate(e.target.value)}
              min={new Date().toISOString().slice(0, 16)} />
            <small style={{ color:'#aaa', marginTop:'6px', display:'block' }}>
              O disparo iniciará automaticamente na data e hora selecionadas
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <button onClick={() => setScheduleModal(false)}
            style={{ border:'1px solid #dee2e6', background:'none', borderRadius:'8px', padding:'8px 20px', cursor:'pointer' }}>
            Cancelar
          </button>
          <button onClick={handleSchedule} disabled={!scheduleDate || launching}
            style={{ background: scheduleDate && !launching ? '#7366ff' : '#c4b5fd', color:'#fff', border:'none', borderRadius:'8px', padding:'8px 20px', cursor: scheduleDate && !launching ? 'pointer' : 'not-allowed', fontWeight:500 }}>
            {launching ? 'Agendando...' : 'Confirmar agendamento'}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
}
